import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Marke } from '../components/Marke'
import {
  istStilleAbsage,
  netconCloud,
  netconCloudAbschliessen,
  NOTAUSGANG,
  zurueckNachAnmeldung,
} from '../lib/netcon-cloud'
import { router } from '../router'

/**
 * **Die Zwischenseite nach dem Rückweg von NetCon.Cloud** (01.09.2026,
 * [E-289](../../../docs/ENTSCHEIDUNGEN.md#e-289)).
 *
 * Sie ist eine knappe Sekunde zu sehen: Code gegen Token tauschen, Token gegen eine
 * Work-Sitzung, dann weiter zu „Mein Tag". Trotzdem hat sie eine eigene Ansicht und nicht nur
 * einen Spinner — geht etwas schief, ist **das hier** die Stelle, an der es jemand erfährt.
 * Eine weiße Seite mit einem Fehler in der Konsole wäre die Alternative.
 */
export function NetconRueckweg() {
  const [fehler, setFehler] = useState<string | null>(null)
  const queryClient = useQueryClient()
  /**
   * **Genau einmal.**
   *
   * React führt Effekte im Strict-Modus zweimal aus, und der zweite Lauf hätte einen bereits
   * verbrauchten Code in der Hand — Keycloak lehnt ihn ab, und aus einer gelungenen Anmeldung
   * würde eine Fehlermeldung. Ein `useRef` ist hier die richtige Sperre, weil er den zweiten
   * Lauf überlebt.
   */
  const gelaufen = useRef(false)

  useEffect(() => {
    if (gelaufen.current) return
    gelaufen.current = true

    /**
     * **Eine stille Absage ist kein Fehler** ([E-290](../../../docs/ENTSCHEIDUNGEN.md#e-290)).
     *
     * Kam der Rückweg mit `error=login_required`, hat Keycloak auf `prompt=none` geantwortet
     * *„kenne ich nicht"*. Das hat niemand ausgelöst und niemand muss es lesen: zurück zur
     * Maske, ohne roten Kasten. Der Sperrvermerk steht schon im `sessionStorage`, ein zweiter
     * Versuch kommt also nicht.
     */
    if (istStilleAbsage(location.search)) {
      void netconCloud().clearStaleState()
      /**
       * **Auch die Absage verbraucht den Vermerk.** Bliebe er liegen, schickte er irgendwann
       * eine ganz andere Anmeldung auf eine Seite, die längst niemanden mehr interessiert.
       */
      void router.navigate({ to: zurueckNachAnmeldung() ?? '/', replace: true })
      return
    }

    void netconCloudAbschliessen().then(async (meldung) => {
      if (meldung) {
        setFehler(meldung)
        return
      }
      /**
       * Die Sitzung ist neu — alles, was der Client über „wer bin ich" zu wissen glaubte, ist
       * überholt. `invalidateQueries()` ohne Schlüssel, dasselbe wie beim Abmelden.
       */
      await queryClient.invalidateQueries()
      /**
       * **Zurück auf die Seite, von der es losging** – wenn es eine gibt (17.09.2026,
       * [Nachtrag zu E-364](../../../docs/ENTSCHEIDUNGEN.md#e-364)).
       *
       * Nur der nachgeholte Versuch nach dem Rückweg legt einen solchen Vermerk an. Ohne ihn
       * landete der Mensch auf „Mein Tag", nachdem das Portal ihn eben erst sorgfältig auf seine
       * Seite zurückgebracht hat – der Umweg wäre sichtbar geworden, obwohl er unsichtbar sein
       * soll. Für jeden anderen Weg hierher bleibt `/` die Antwort wie bisher.
       */
      await router.navigate({ to: zurueckNachAnmeldung() ?? '/' })
    })
  }, [queryClient])

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <Marke className="size-12 rounded-[14px]" />
        {fehler ? (
          <>
            <h1 className="text-[17px] font-bold">Das hat nicht geklappt</h1>
            <p className="text-[13.5px] text-ink2">{fehler}</p>
            <a
              href={`/?${NOTAUSGANG}=1`}
              className="rounded-lg bg-ink px-3.5 py-2 text-[13px] font-semibold text-white"
            >
              Zur Anmeldung
            </a>
          </>
        ) : (
          <>
            <h1 className="text-[17px] font-bold">Einen Moment …</h1>
            <p className="text-[13.5px] text-muted">
              NetCon.Cloud hat dich bestätigt. Work richtet die Sitzung ein.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
